import { notifications } from '@mantine/notifications';
import { IconFolders } from '@tabler/icons-react';
import type { ProjectAccessLevel, ProjectProduct } from '@xver/apis/v1/projects/model';
import { setActiveProject } from '@xver/store/project/project.actions';
import {
    selectActiveProjectId,
    selectIsProjectResolved,
} from '@xver/store/project/project.reducer';
import { accessAtLeast } from '@xver/utils/projects';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import { useProjectsQuery } from './useProjectsQuery';

/**
 * Cheap redux-only read for query hooks: put `activeProjectId` in the key and gate the query with
 * `enabled: isResolved`.
 */
export function useActiveProjectScope() {
    const activeProjectId = useAppSelector(selectActiveProjectId);
    const isResolved = useAppSelector(selectIsProjectResolved);
    return { activeProjectId, isResolved };
}

/**
 * `can` is false until resolved, and true for everything when resolved without a project (projects
 * API unavailable: requests go header-less to the caller's own default project).
 */
export function useActiveProject() {
    const { activeProjectId, isResolved } = useActiveProjectScope();
    const { data: projects } = useProjectsQuery();

    const project = activeProjectId
        ? projects?.find((candidate) => candidate.id === activeProjectId)
        : undefined;
    const access = project?.access;
    const isLegacy = isResolved && activeProjectId === null;

    const can = useCallback(
        (product: ProjectProduct, level: ProjectAccessLevel) =>
            isLegacy || accessAtLeast(access?.[product], level),
        [isLegacy, access]
    );

    return { project, access, can, activeProjectId, isResolved };
}

/**
 * The caller's access to `product` in the active project, for page-level gating: undefined until
 * resolved (or while the project list loads), `manage` when resolved without a project.
 */
export function useProductAccess(product: ProjectProduct): ProjectAccessLevel | undefined {
    const { project, activeProjectId, isResolved } = useActiveProject();
    if (!isResolved) return undefined;
    if (activeProjectId === null) return 'manage';
    return project?.access[product];
}

/**
 * For resource detail pages: when the loaded resource belongs to another project the caller is a
 * member of, make that project active and say so. Pass undefined while the resource is loading.
 * Runs once per resource project, so a later manual switch is not undone.
 */
export function useAlignActiveProject(resourceProjectId: string | undefined) {
    const { t } = useTranslation('projects');
    const dispatch = useAppDispatch();
    const userId = useAppSelector((state) => state.auth.user?.id);
    const { activeProjectId, isResolved } = useActiveProjectScope();
    const { data: projects } = useProjectsQuery();
    const handledFor = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!resourceProjectId || !isResolved || userId === undefined || !projects) return;
        if (handledFor.current === resourceProjectId) return;
        handledFor.current = resourceProjectId;
        if (resourceProjectId === activeProjectId) return;

        const target = projects.find((project) => project.id === resourceProjectId);
        if (!target) return;

        dispatch(setActiveProject({ userId, projectId: target.id }));
        notifications.show({
            title: t('aligned.title'),
            message: t('aligned.message', { name: target.name }),
            icon: <IconFolders size={20} aria-hidden="true" />,
        });
    }, [resourceProjectId, isResolved, userId, activeProjectId, projects, dispatch, t]);
}
